import * as React from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"


type StageOption = { id: number; key: string; name: string }

type WorkflowRun = {
  id: number
  batch_id: string | null
  summary: boolean
  status: string
  action_summary: string
  error_message: string | null
  created_at: string
  lead: { id: number; customer_name: string; stage_key: string | null; stage_name: string | null } | null
}

type WorkflowPreview = {
  batch_id: string
  dry_run_at: string
  candidate_count: number
  counts: { dry_run: number; applied: number; skipped: number; failed: number }
  message: string
  trigger_type: string
  trigger_label: string
  conditions_summary: string
  action_summary: string
  candidates: WorkflowRun[]
} | null

type WorkflowBatch = {
  batch_id: string
  created_at: string
  latest_status: string
  candidate_count: number
  counts: { dry_run: number; applied: number; skipped: number; failed: number }
  message: string
  leads: WorkflowRun[]
}

type Workflow = {
  id: number
  name: string
  description: string | null
  enabled: boolean
  status: string
  graph_state_label: string
  graph_draft_different_from_published: boolean
  version: number
  published_version: number | null
  trigger_type: string
  trigger_label: string
  execution_mode: string
  execution_mode_label: string
  conditions_json: string
  actions_json: string
  conditions_summary: string
  action_summary: string
  last_run_at: string | null
  latest_applied_at: string | null
  last_scheduled_run_at: string | null
  schedule_interval_minutes: number | null
  max_leads_per_run: number
  scheduler_summary: string
  scheduler_warning: string
  scheduler_due: boolean
  next_scheduled_run_at: string | null
  created_by_email: string | null
  updated_by_email: string | null
  recent_run_count: number
  applied_run_count: number
  last_run_status: string | null
  last_run_summary: string | null
}

type Props = {
  workflow: Workflow
  latest_applied_batch: WorkflowBatch | null
  latest_dry_run_preview: WorkflowPreview
  recent_batches: WorkflowBatch[]
  workflow_errors: string[]
  flash_notice: string | null
  trigger_types: string[]
  execution_modes: string[]
  stage_options: StageOption[]
  action_types: string[]
  run_confirmation_window_minutes: number
}

export default function WorkflowsShow({ workflow, latest_applied_batch, latest_dry_run_preview, recent_batches, workflow_errors, flash_notice, trigger_types, execution_modes, stage_options, action_types, run_confirmation_window_minutes }: Props) {
  const [runNowOpen, setRunNowOpen] = React.useState(false)
  const [runConfirmation, setRunConfirmation] = React.useState("")
  const form = useForm({ workflow: {
    name: workflow.name,
    description: workflow.description ?? "",
    enabled: workflow.enabled,
    trigger_type: workflow.trigger_type,
    execution_mode: workflow.execution_mode,
    schedule_interval_minutes: workflow.schedule_interval_minutes?.toString() ?? "",
    max_leads_per_run: workflow.max_leads_per_run.toString(),
    conditions_json: workflow.conditions_json,
    actions_json: workflow.actions_json,
  }})

  const previewCandidateCount = latest_dry_run_preview?.candidate_count ?? 0
  const expectedConfirmation = `RUN ${previewCandidateCount}`
  const latestAppliedAt = workflow.latest_applied_at ? new Date(workflow.latest_applied_at).getTime() : null
  const previewFresh = latest_dry_run_preview ? Date.now() - new Date(latest_dry_run_preview.dry_run_at).getTime() <= run_confirmation_window_minutes * 60_000 && (latestAppliedAt === null || new Date(latest_dry_run_preview.dry_run_at).getTime() > latestAppliedAt) : false
  const runEligibility = getRunEligibility(workflow.enabled, latest_dry_run_preview, previewFresh, previewCandidateCount)
  const canRunNow = runEligibility.canRunNow

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.patch(`/workflows/${workflow.id}`, { preserveScroll: true })
  }

  const openRunNow = () => {
    if (!canRunNow) return
    setRunConfirmation("")
    setRunNowOpen(true)
  }

  const submitRunNow = (event: React.FormEvent) => {
    event.preventDefault()
    if (!canRunNow || runConfirmation !== expectedConfirmation) return
    router.post(`/workflows/${workflow.id}/run`, { confirmation: runConfirmation }, { preserveScroll: true, onFinish: () => setRunNowOpen(false) })
  }

  return (
    <>
      <Head title={`${workflow.name} | LeadFlow`}>
        <meta name="description" content={`Inspect and manage the ${workflow.name} workflow.`} />
        <meta property="og:title" content={`${workflow.name} | LeadFlow`} />
        <meta property="og:description" content={`Inspect and manage the ${workflow.name} workflow.`} />
      </Head>
      <AppShell wide>
        <PageHeader
          title={workflow.name}
          description={workflow.description || workflow.trigger_label}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="secondary"><Link href="/workflows">Back to workflows</Link></Button>
              <Button asChild variant="secondary"><Link href={`/workflows/${workflow.id}/builder`}>Visual builder</Link></Button>
              <Button type="button" variant="secondary" onClick={() => router.post(`/workflows/${workflow.id}/dry_run`, {}, { preserveScroll: true })}>Dry run</Button>
              <Button type="button" onClick={openRunNow} disabled={!canRunNow}>Run now</Button>
            </div>
          }
        />

        <div className="mt-4 rounded-md border border-hairline bg-page px-3 py-2 text-xs text-ink-muted">
          {runEligibility.message}
        </div>

        {flash_notice && <p className="mt-6 rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body">{flash_notice}</p>}
        {workflow_errors.length > 0 && <ErrorBox errors={workflow_errors} />}

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_0.95fr]">
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={workflow.enabled ? "accent" : "muted"}>{workflow.enabled ? "Enabled" : "Disabled"}</Badge>
              <Badge tone={workflow.status === "published" ? "accent" : workflow.status === "inactive" ? "signal" : "muted"}>{humanize(workflow.status)}</Badge>
              <Badge tone={workflow.graph_draft_different_from_published ? "signal" : "muted"}>{workflow.graph_draft_different_from_published ? "Draft changes" : workflow.graph_state_label}</Badge>
              <Badge tone="neutral">{workflow.trigger_label}</Badge>
              <Badge tone={workflow.execution_mode === "scheduled" && workflow.scheduler_due ? "accent" : workflow.execution_mode === "paused" ? "signal" : "muted"}>{workflow.execution_mode_label}</Badge>
              <Badge tone="muted">Version {workflow.version}</Badge>
              <Badge tone="muted">{workflow.recent_run_count} runs</Badge>
            </div>
            <div className="mt-3 rounded-md border border-hairline bg-page px-3 py-2 text-xs text-ink-muted">
              <p className="font-semibold uppercase tracking-wide text-ink-muted">Scheduler warning</p>
              <p className="mt-1">{workflow.scheduler_warning}</p>
            </div>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2">
              <Detail label="Created by" value={workflow.created_by_email || "—"} />
              <Detail label="Updated by" value={workflow.updated_by_email || "—"} />
              <Detail label="Last run" value={workflow.last_run_at || "None"} />
              <Detail label="Last status" value={workflow.last_run_status || "None"} />
              <Detail label="Execution mode" value={workflow.execution_mode_label} />
              <Detail label="Schedule interval" value={workflow.schedule_interval_minutes ? `${workflow.schedule_interval_minutes} minutes` : "Not scheduled"} />
              <Detail label="Max leads per run" value={workflow.max_leads_per_run.toString()} />
              <Detail label="Last scheduled run" value={workflow.last_scheduled_run_at || "None"} />
              <Detail label="Next scheduled run" value={workflow.next_scheduled_run_at || "Not scheduled"} />
            </dl>
            <div className="mt-4 rounded-md border border-hairline bg-page p-4 text-sm text-ink-muted">
              <p className="font-medium text-ink-display">What this workflow does</p>
              <p className="mt-1">{workflow.conditions_summary || "No conditions configured"}</p>
              <p className="mt-1 text-xs text-ink-muted">{workflow.graph_draft_different_from_published ? "Draft changes are waiting in the visual builder." : "The draft graph matches the published version."}</p>
              <p className="mt-1">{workflow.action_summary || "No actions configured"}</p>
            </div>

            <form className="mt-6 grid gap-4" onSubmit={submit}>
              <label className="grid gap-2">Name<Input required value={form.data.workflow.name} onChange={(event) => form.setData("workflow", { ...form.data.workflow, name: event.target.value })} /></label>
              <label className="grid gap-2">Description<textarea className="form-control form-control-textarea" value={form.data.workflow.description} onChange={(event) => form.setData("workflow", { ...form.data.workflow, description: event.target.value })} /></label>
              <label className="grid gap-2">Trigger<Select value={form.data.workflow.trigger_type} onChange={(event) => form.setData("workflow", { ...form.data.workflow, trigger_type: event.target.value })}>{trigger_types.map((trigger) => <option key={trigger} value={trigger}>{humanize(trigger)}</option>)}</Select></label>
              <label className="grid gap-2">Execution mode<Select value={form.data.workflow.execution_mode} onChange={(event) => form.setData("workflow", { ...form.data.workflow, execution_mode: event.target.value })}>{execution_modes.map((mode) => <option key={mode} value={mode}>{humanize(mode)}</option>)}</Select></label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-2">Schedule interval minutes<Input type="number" min="1" value={form.data.workflow.schedule_interval_minutes} onChange={(event) => form.setData("workflow", { ...form.data.workflow, schedule_interval_minutes: event.target.value })} placeholder="Only used when scheduled" /></label>
                <label className="grid gap-2">Max leads per run<Input type="number" min="1" value={form.data.workflow.max_leads_per_run} onChange={(event) => form.setData("workflow", { ...form.data.workflow, max_leads_per_run: event.target.value })} /></label>
              </div>
              <label className="flex items-center gap-2 text-sm font-normal text-ink-body"><input type="checkbox" checked={form.data.workflow.enabled} onChange={(event) => form.setData("workflow", { ...form.data.workflow, enabled: event.target.checked })} />Enabled</label>
              <label className="grid gap-2">Conditions JSON<textarea className="form-control form-control-textarea min-h-[11rem] font-mono text-xs" value={form.data.workflow.conditions_json} onChange={(event) => form.setData("workflow", { ...form.data.workflow, conditions_json: event.target.value })} /></label>
              <label className="grid gap-2">Actions JSON<textarea className="form-control form-control-textarea min-h-[12rem] font-mono text-xs" value={form.data.workflow.actions_json} onChange={(event) => form.setData("workflow", { ...form.data.workflow, actions_json: event.target.value })} /></label>
              <div className="rounded-md border border-hairline bg-surface p-4 text-xs text-ink-muted">
                <p className="font-medium text-ink-display">Helpers</p>
                <p className="mt-1">Stage keys: {stage_options.map((stage) => stage.key).join(", ")}</p>
                <p className="mt-1">Supported actions: {action_types.join(", ")}</p>
                <p className="mt-1">CTM SMS is reserved for later and is not active in this pass.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button disabled={form.processing}>Save workflow</Button>
                <Button type="button" variant="secondary" onClick={() => router.patch(`/workflows/${workflow.id}/toggle`, {}, { preserveScroll: true })}>{workflow.enabled ? "Disable" : "Enable"}</Button>
              </div>
            </form>
          </section>

          <div className="space-y-6">
            {latest_applied_batch && (
              <section className="rounded-lg border border-hairline bg-page p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2>Latest applied batch</h2>
                  <Badge tone={latest_applied_batch.latest_status === "failed" ? "signal" : "accent"}>{latest_applied_batch.latest_status}</Badge>
                </div>
                <div className="mt-4 space-y-4">
                  <p className="rounded-md border border-hairline bg-surface px-3 py-2 text-sm text-ink-body">{latest_applied_batch.message}</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Badge tone="muted">Applied leads: {latest_applied_batch.candidate_count}</Badge>
                    <Badge tone="muted">Applied: {latest_applied_batch.counts.applied}</Badge>
                    <Badge tone="muted">Skipped: {latest_applied_batch.counts.skipped}</Badge>
                    <Badge tone="muted">Failed: {latest_applied_batch.counts.failed}</Badge>
                    <Badge tone="muted">Batch: {shortBatchId(latest_applied_batch.batch_id)}</Badge>
                  </div>
                  <div className="space-y-3">
                    {latest_applied_batch.leads.slice(0, 3).map((lead) => (
                      <article key={lead.id} className="rounded-md border border-hairline bg-page p-4 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <Link href={`/dashboard?lead_id=${lead.lead!.id}`} className="font-semibold text-ink-display no-underline">{lead.lead!.customer_name}</Link>
                            <p className="text-xs text-ink-muted">Lead #{lead.lead!.id} · {lead.lead!.stage_name || "Lead unavailable"}</p>
                          </div>
                          <Badge tone="muted">{lead.status}</Badge>
                        </div>
                        <p className="mt-2 text-xs text-ink-muted">{lead.action_summary}</p>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            )}

            <section className="rounded-lg border border-hairline bg-page p-5">
              <div className="flex items-center justify-between gap-3">
                <h2>Latest dry-run preview</h2>
                <Badge tone={latest_dry_run_preview && canRunNow ? "accent" : "muted"}>{latest_dry_run_preview ? `${latest_dry_run_preview.candidate_count} leads` : "No dry-run yet"}</Badge>
              </div>
              {!latest_dry_run_preview ? (
                <p className="mt-4 text-sm text-ink-muted">Run a dry-run to preview matching leads.</p>
              ) : (
                <div className="mt-4 space-y-4">
                  <p className="rounded-md border border-hairline bg-surface px-3 py-2 text-sm text-ink-body">{latest_dry_run_preview.message}</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Badge tone="muted">Total matching leads: {latest_dry_run_preview.candidate_count}</Badge>
                    <Badge tone="muted">Dry-run: {latest_dry_run_preview.counts.dry_run}</Badge>
                    <Badge tone="muted">Applied: {latest_dry_run_preview.counts.applied}</Badge>
                    <Badge tone="muted">Skipped: {latest_dry_run_preview.counts.skipped}</Badge>
                    <Badge tone="muted">Failed: {latest_dry_run_preview.counts.failed}</Badge>
                    <Badge tone="muted">Batch: {shortBatchId(latest_dry_run_preview.batch_id)}</Badge>
                  </div>
                  <p className="text-xs text-ink-muted">{latest_dry_run_preview.trigger_label} · {latest_dry_run_preview.conditions_summary || "No conditions configured"} · {latest_dry_run_preview.action_summary || "No actions configured"}</p>
                  {latest_dry_run_preview.candidates.length > 0 ? (
                    <div className="space-y-3">
                      {latest_dry_run_preview.candidates.map((candidate) => (
                        <article key={candidate.id} className="rounded-md border border-hairline bg-page p-4 text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <Link href={`/dashboard?lead_id=${candidate.lead!.id}`} className="font-semibold text-ink-display no-underline">{candidate.lead!.customer_name}</Link>
                              <p className="text-xs text-ink-muted">Lead #{candidate.lead!.id} · {candidate.lead!.stage_name || "Lead unavailable"}</p>
                            </div>
                            <Badge tone="muted">{candidate.status}</Badge>
                          </div>
                          <p className="mt-2 text-xs text-ink-muted">{candidate.action_summary}</p>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-ink-muted">No matching leads found for this workflow.</p>
                  )}
                </div>
              )}
            </section>

            <section className="rounded-lg border border-hairline bg-page p-5">
              <div className="flex items-center justify-between gap-3">
                <h2>Recent batches</h2>
                <Badge tone="muted">{recent_batches.length}</Badge>
              </div>
              <div className="mt-4 space-y-3">
                {recent_batches.length === 0 && <p className="text-sm text-ink-muted">No workflow runs yet.</p>}
                {recent_batches.map((batch) => (
                  <article key={batch.batch_id} className="rounded-md border border-hairline bg-surface p-4 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={batch.latest_status === "failed" ? "signal" : batch.latest_status === "applied" ? "accent" : "muted"}>{batch.latest_status}</Badge>
                      <span className="text-ink-muted">{formatTimestamp(batch.created_at)}</span>
                      <Badge tone="muted">{batch.candidate_count} leads</Badge>
                      <Badge tone="muted">Batch {shortBatchId(batch.batch_id)}</Badge>
                    </div>
                    <p className="mt-2 text-ink-display">{batch.message}</p>
                    <p className="mt-1 text-xs text-ink-muted">Dry-run: {batch.counts.dry_run} · Applied: {batch.counts.applied} · Skipped: {batch.counts.skipped} · Failed: {batch.counts.failed}</p>
                    <div className="mt-3 space-y-2">
                      {batch.leads.slice(0, 3).map((lead) => (
                        <p key={lead.id} className="text-xs text-ink-muted">
                          <Link href={`/dashboard?lead_id=${lead.lead!.id}`} className="text-accent no-underline">{lead.lead!.customer_name}</Link> · {lead.lead!.stage_name || "Lead unavailable"} · {lead.action_summary}
                        </p>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </div>
      </AppShell>

      {runNowOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
          <div className="w-full max-w-xl rounded-lg border border-hairline bg-page p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-ink-display">Confirm Run Now</h3>
            <p className="mt-2 text-sm text-ink-body">This will apply actions to {previewCandidateCount} matching leads. This may move stages or create follow-ups/internal notes.</p>
            <p className="mt-2 text-sm text-ink-muted">Lead data may have changed since the dry-run.</p>
            <p className="mt-2 text-sm text-ink-muted">Type <span className="font-mono font-semibold text-ink-display">{expectedConfirmation}</span> to confirm.</p>
            <form className="mt-4 grid gap-4" onSubmit={submitRunNow}>
              <Input value={runConfirmation} onChange={(event) => setRunConfirmation(event.target.value)} placeholder={expectedConfirmation} autoFocus />
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setRunNowOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={runConfirmation !== expectedConfirmation}>Run now</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-sm text-ink-muted">{label}</dt><dd className="text-sm text-ink-body">{value}</dd></div>
}

function ErrorBox({ errors }: { errors: string[] }) {
  return <div className="mt-6 rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger-display"><p className="font-medium">Please fix the workflow form:</p><ul className="mt-2 list-disc space-y-1 pl-5">{errors.map((error) => <li key={error}>{error}</li>)}</ul></div>
}

function humanize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function formatTimestamp(value: string) {
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
}

function getRunEligibility(enabled: boolean, latestDryRun: WorkflowPreview, previewFresh: boolean, previewCandidateCount: number) {
  if (!enabled) {
    return { canRunNow: false, message: "Enable this workflow before running it." }
  }

  if (!latestDryRun) {
    return { canRunNow: false, message: "Run a dry-run first." }
  }

  if (!previewFresh) {
    return { canRunNow: false, message: "Dry-run expired. Run another dry-run." }
  }

  if (previewCandidateCount === 0) {
    return { canRunNow: false, message: "No matching leads in latest dry-run." }
  }

  return { canRunNow: true, message: `Run Now available. Type RUN ${previewCandidateCount} to confirm.` }
}

function shortBatchId(value: string) {
  return value.split("-")[0]
}
